import React, { useEffect } from "react";
import styled from "styled-components";
import { Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  DatabaseIcon,
  CloudIcon,
  CodeIcon,
  ProjectDiagramIcon,
  LightbulbIcon,
  ToolsIcon,
} from "./IconComponents";
import SEO from "./SEO";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const ServiceTypesSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServiceTypeCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const ServiceTypeTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceTypeDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ServiceTypeFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Prozessoptimierung: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Effizientere Geschäftsprozesse",
      description:
        "Wir analysieren Ihre Abläufe unternehmensweit und identifizieren Engpässe, Medienbrüche und unnötige Arbeitsschritte, um Durchlaufzeiten spürbar zu verkürzen.",
    },
    {
      icon: <LockIcon />,
      title: "Weniger Fehler, mehr Qualität",
      description:
        "Durch optimierte Workflows, klare Freigabeprozesse und automatisierte Prüfungen reduzieren Sie manuelle Fehler und sichern konsistente Datenqualität.",
    },
    {
      icon: <UsersIcon />,
      title: "Höhere Nutzerakzeptanz",
      description:
        "Schlanke, an die Praxis angepasste Prozesse sorgen dafür, dass Ihre Mitarbeiter Ihre Systeme gerne und effizient nutzen, statt Workarounds zu suchen.",
    },
    {
      icon: <LightbulbIcon />,
      title: "Volles Potenzial Ihrer Systeme",
      description:
        "Viele Unternehmen nutzen nur einen Bruchteil der Möglichkeiten ihrer bestehenden Software. Wir heben ungenutzte Funktionen und Automatisierungspotenziale.",
    },
  ];

  const process = [
    {
      title: "Prozessanalyse",
      description:
        "Wir erfassen Ihre bestehenden Abläufe, befragen Beteiligte und identifizieren Engpässe, Medienbrüche und Doppelarbeit.",
    },
    {
      title: "Potenzialbewertung",
      description:
        "Bewertung der identifizierten Schwachstellen nach Aufwand und Wirkung, um die vielversprechendsten Optimierungen zu priorisieren.",
    },
    {
      title: "Umsetzung",
      description:
        "Neugestaltung von Workflows, Formularen und Freigabeprozessen sowie Anbindung an bestehende Systeme – Schritt für Schritt und praxisnah.",
    },
    {
      title: "Kontrolle & Feinschliff",
      description:
        "Messung der erzielten Verbesserungen anhand definierter Kennzahlen und kontinuierliche Nachjustierung der Prozesse.",
    },
  ];

  const benefits = [
    {
      title: "Kosteneinsparungen",
      description:
        "Reduzierung der Betriebskosten durch schlankere Abläufe und Vermeidung unnötiger Arbeitsschritte.",
    },
    {
      title: "Schnellere Durchlaufzeiten",
      description:
        "Deutlich verkürzte Bearbeitungszeiten durch klar strukturierte, nahtlose Workflows.",
    },
    {
      title: "Skalierbarkeit",
      description:
        "Optimierte Prozesse lassen sich flexibel an wachsende Anforderungen und Datenmengen anpassen.",
    },
    {
      title: "Compliance & Nachverfolgbarkeit",
      description:
        "Lückenlose Dokumentation und Protokollierung aller Prozessschritte für Auditierbarkeit und Nachvollziehbarkeit.",
    },
    {
      title: "Bessere Datenqualität",
      description:
        "Konsistente Datenerfassung und -verarbeitung liefert eine verlässliche Grundlage für Entscheidungen.",
    },
    {
      title: "Höhere Mitarbeiterzufriedenheit",
      description:
        "Weniger Frustration durch umständliche Abläufe – Ihr Team kann sich auf wertschöpfende Aufgaben konzentrieren.",
    },
  ];

  const technologies = [
    {
      icon: <ProjectDiagramIcon />,
      name: "Prozessmodellierung",
      description:
        "Visualisierung und Dokumentation von Abläufen (BPMN, Flussdiagramme)",
    },
    {
      icon: <ToolsIcon />,
      name: "Workflow-Automatisierung",
      description:
        "Automatisierte Freigaben, Benachrichtigungen und Aufgabenverteilung",
    },
    {
      icon: <CloudIcon />,
      name: "Cloud-Integration",
      description:
        "Anbindung von ERP-, CRM- und Fachanwendungen über Schnittstellen",
    },
    {
      icon: <DatabaseIcon />,
      name: "Business Intelligence",
      description: "Reporting und Dashboards zur Prozess- und Leistungsmessung",
    },
    {
      icon: <CodeIcon />,
      name: "Individuelle Erweiterungen",
      description:
        "Maßgeschneiderte Anpassungen und Schnittstellen für Ihre Systemlandschaft",
    },
  ];

  const caseStudies = [
    {
      title: "Optimierung der Auftragsabwicklung",
      description:
        "Analyse und Neugestaltung des Auftragsprozesses eines Fertigungsunternehmens, von der Angebotserstellung bis zur Rechnungsstellung.",
      result: {
        title: "Ergebnisse:",
        description:
          "45% kürzere Durchlaufzeiten, 30% weniger manuelle Nacharbeit, deutlich verbesserte Termintreue",
      },
    },
    {
      title: "Effizientere Freigabeprozesse",
      description:
        "Einführung digitaler, automatisierter Freigabeworkflows für Beschaffung und Rechnungsprüfung in einem mittelständischen Unternehmen.",
      result: {
        title: "Ergebnisse:",
        description:
          "60% schnellere Freigaben, lückenlose Nachvollziehbarkeit, spürbar reduzierter administrativer Aufwand",
      },
    },
    {
      title: "Transparenz im Kundenservice",
      description:
        "Vereinheitlichung und Automatisierung der Ticketbearbeitung inklusive Reporting für ein Dienstleistungsunternehmen.",
      result: {
        title: "Ergebnisse:",
        description:
          "35% schnellere Reaktionszeiten, klare Prozessstatus in Echtzeit, höhere Kundenzufriedenheit",
      },
    },
  ];

  const serviceTypes = [
    {
      title: "Prozessanalyse & -design",
      description:
        "Aufnahme und Bewertung bestehender Abläufe sowie Konzeption schlanker, praxistauglicher Prozesse.",
      features: [
        "Ist-Analyse und Schwachstellenermittlung",
        "Priorisierung nach Aufwand und Nutzen",
        "Neugestaltung von Workflows",
        "Dokumentation und Prozesslandkarten",
        "Change-Management-Begleitung",
      ],
    },
    {
      title: "Workflow-Automatisierung",
      description:
        "Digitalisierung und Automatisierung wiederkehrender Abläufe, Freigaben und Benachrichtigungen.",
      features: [
        "Automatisierte Freigabeprozesse",
        "Formular- und Vorlagenerstellung",
        "Schnittstellen zu bestehenden Systemen",
        "Benachrichtigungen und Eskalationen",
        "Monitoring und Fehlerbehandlung",
      ],
    },
    {
      title: "Reporting & kontinuierliche Verbesserung",
      description:
        "Messbarkeit von Prozessen durch Kennzahlen, Dashboards und regelmäßige Optimierungszyklen.",
      features: [
        "KPI-Definition und Dashboards",
        "Echtzeit-Reporting",
        "Regelmäßige Prozessreviews",
        "Kontinuierliche Optimierung",
        "Schulung Ihrer Mitarbeiter",
      ],
    },
  ];

  const faqs = [
    {
      question: "Für welche Unternehmen eignet sich Prozessoptimierung?",
      answer:
        "Prozessoptimierung lohnt sich für Unternehmen jeder Größe und Branche, sobald wiederkehrende Abläufe wie Freigaben, Bestellungen, Kundenanfragen oder interne Genehmigungen viel manuellen Aufwand verursachen. Wir passen unser Vorgehen individuell an Ihre Ausgangssituation an.",
    },
    {
      question: "Muss ich dafür meine bestehende Software wechseln?",
      answer:
        "Nein, in den meisten Fällen optimieren wir Prozesse innerhalb Ihrer vorhandenen Systeme und heben ungenutzte Funktionen und Automatisierungsmöglichkeiten. Ein Systemwechsel ist nur nötig, wenn die vorhandene Lösung tatsächlich an ihre Grenzen stößt.",
    },
    {
      question: "Wie lange dauert eine Prozessoptimierung?",
      answer:
        "Einfache Prozessanpassungen sind oft innerhalb weniger Wochen umsetzbar, während umfassendere Optimierungsprojekte über mehrere Monate laufen können. Wir liefern in der Regel schon früh erste spürbare Verbesserungen.",
    },
    {
      question: "Wie wird der Erfolg der Optimierung gemessen?",
      answer:
        "Gemeinsam mit Ihnen definieren wir vorab messbare Kennzahlen wie Durchlaufzeiten, Fehlerquoten oder Bearbeitungskosten. Nach der Umsetzung vergleichen wir diese Werte, um den erzielten Nutzen transparent zu belegen.",
    },
    {
      question: "Werden meine Mitarbeiter in den Prozess eingebunden?",
      answer:
        "Ja, die Einbindung der Mitarbeiter ist ein zentraler Erfolgsfaktor. Wir führen Interviews und Workshops durch, um praxisnahe Lösungen zu entwickeln und die Akzeptanz der neuen Prozesse sicherzustellen.",
    },
  ];

  return (
    <PageContainer>
      <SEO
        title="Prozessoptimierung für Unternehmen"
        description="Optimieren Sie Ihre Geschäftsprozesse: schnellere Workflows, weniger Fehler, mehr Transparenz. Byte Forge analysiert und optimiert Ihre Prozesse – unabhängig von der eingesetzten Software."
        path="/prozessoptimierung-dynamics365"
        keywords="Prozessoptimierung, Geschäftsprozesse optimieren, Workflow-Optimierung, Prozessanalyse, Effizienzsteigerung Unternehmen"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Prozessoptimierung für <span>Ihr Unternehmen</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Holen Sie das volle Potenzial aus Ihren Geschäftsprozessen heraus. Wir
          analysieren Ihre Abläufe und optimieren Workflows, Automatisierungen
          und Formulare für mehr Effizienz, Transparenz und Datenqualität –
          unabhängig davon, welche Systeme Sie einsetzen.
        </SectionSubtitle>
      </Container>
      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>Prozessoptimierung</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>Vorgehen</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Methoden & Werkzeuge</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>Optimierungsprojekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <ServiceTypesSection>
        <Container>
          <SectionTitle>
            Unsere <span>Leistungen</span>
          </SectionTitle>
          <ServiceTypeGrid>
            {serviceTypes.map((service, index) => (
              <ServiceTypeCard key={index}>
                <ServiceTypeTitle>{service.title}</ServiceTypeTitle>
                <ServiceTypeDescription>
                  {service.description}
                </ServiceTypeDescription>
                <ServiceTypeFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ServiceTypeFeatures>
              </ServiceTypeCard>
            ))}
          </ServiceTypeGrid>
        </Container>
      </ServiceTypesSection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit, Ihre <span>Prozesse zu optimieren?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam Ihre Geschäftsprozesse analysieren und das
            volle Potenzial Ihrer bestehenden Systeme ausschöpfen.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>

    </PageContainer>
  );
};

export default Prozessoptimierung;
